import { AfterViewInit, ChangeDetectorRef, Component, Inject, OnInit, TemplateRef, viewChild } from '@angular/core';
import {
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';

import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { TsSnackbarService } from 'tsui';
import { TsTabDefinition } from 'tsui/lib/ts-tab-group/ts-tab-group.component';
import { Actions, Store, ofActionCompleted, ofActionErrored } from '@ngxs/store';
import { Folders } from '../state/actions';
import { AccountState } from 'app/state/account/state';
import { filter, tap, switchMap } from 'rxjs';
import { Account } from 'app/core/models/account.types';
import { FileResource, Document } from '../folders.types';
import { DELETE_LAST_DOCUMENT_CONFIRMATION } from '../folders.constants';
import { ConfirmService } from 'app/shared/components/widgets/dialog/confirm/confirm.service';

@Component({
  selector: 'folders-tab-view-file',
  templateUrl: './view-file.component.html',
  styleUrls: ['./view-file.component.scss'],
})
export class FoldersTabViewFileComponent extends OnDestroyMixin implements OnInit, AfterViewInit {
  localDocsTemplate = viewChild<TemplateRef<unknown>>('localDocsTemplate');
  integrationDocsTemplate = viewChild<TemplateRef<unknown>>('integrationDocsTemplate');

  // Is a WOPI file being edited?
  editWopiFile: boolean;
  // Is the right sidebar expanded?
  expandRightSidebar: boolean;
  selectedFile: FileResource;
  selectedDocument: Document;
  localDocumentsDataSource = new MatTableDataSource<Document>();
  integrationDocumentsDataSource = new MatTableDataSource<Document>();
  localDocumentsColumns = ['version', 'assetable_category_label', 'created_by', 'created_at', 'actions'];
  integrationDocumentsColumns = ['version', 'integrated_storage', 'created_by', 'created_at', 'actions'];
  tabs: TsTabDefinition[];
  account: Account;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { fileResource: FileResource },
    public matDialogRef: MatDialogRef<FoldersTabViewFileComponent>,
    private _changeDetectorRef: ChangeDetectorRef,
    private store: Store,
    private readonly actions$: Actions,
    private confirmService: ConfirmService,
    private readonly snackBar: TsSnackbarService,
  ) {
    super();
    this.account = this.store.selectSnapshot(AccountState.getAccount);
  }

  ngOnInit(): void {
    this.editWopiFile = false;
    this.expandRightSidebar = true;
    this.selectedFile = this.data.fileResource;
    this.loadFile();
    this.setupStateActions();
  }

  ngAfterViewInit(): void {
    this.tabs = [
      { label: 'Documents', template: this.localDocsTemplate() },
      { label: 'Integration Documents', template: this.integrationDocsTemplate() },
    ];
    this._changeDetectorRef.detectChanges();
  }

  private setupStateActions(): void {
    // Fallback to when GetFileById endpoint fails.
    this.actions$
      .pipe(
        untilComponentDestroyed(this),
        ofActionErrored(Folders.GetFileById),
        tap(() => {
          this.snackBar.open('Unable to open the file. Please try again later.', 'error');
          this.discard();
        }),
      )
      .subscribe();

    this.actions$
      .pipe(untilComponentDestroyed(this), ofActionCompleted(Folders.GetFileById))
      .subscribe(() => this.store.dispatch(new Folders.GetFolder(this.selectedFile.folder_resource_id)));
  }

  private loadFile(): void {
    this.store
      .dispatch(new Folders.GetFileById(this.selectedFile.id))
      .pipe(untilComponentDestroyed(this))
      .subscribe(() => {
        const fileResource: FileResource = this.store.selectSnapshot((state) => state.folderState.selectedFile);
        if (fileResource) {
          this.localDocumentsDataSource.data = (fileResource.local_documents ?? []).sort((a, b) =>
            a.id < b.id ? 1 : -1,
          );
          this.integrationDocumentsDataSource.data = (fileResource.integration_documents ?? []).sort((a, b) =>
            a.id < b.id ? 1 : -1,
          );
          this.selectedFile = fileResource;
          this.setSelectedDocument();
        }

        this._changeDetectorRef.markForCheck();
      });
  }

  setSelectedDocument(document?: Document): void {
    if (document) {
      this.selectedDocument = document;
    } else if (this.selectedFile.documents?.length > 0) {
      this.selectedDocument = this.selectedFile.documents[0];
    }
  }

  toggleWopiFile(): void {
    this.editWopiFile = !this.editWopiFile;

    if (this.selectedFile?.wopi?.errors) {
      return;
    }

    if (this.editWopiFile) {
      this._changeDetectorRef.detectChanges();
      // TODO: This is probably not the best way to accomplish posting to the form.
      const form = window.document.getElementById('officeForm') as HTMLFormElement;

      if (form) {
        form.submit();
      }
    }
  }

  downloadDocument(document: Document): void {
    window.open(document.original_url, '_blank');
  }

  deleteDocument(document: Document): void {
    const isLastDocument = (this.selectedFile.documents?.length ?? 0) <= 1;
    this.confirmService
      .openConfirmationModal(isLastDocument ? DELETE_LAST_DOCUMENT_CONFIRMATION : undefined)
      .pipe(
        untilComponentDestroyed(this),
        filter((result: boolean) => result),
        switchMap(() =>
          isLastDocument
            ? this.store.dispatch(new Folders.DeleteFile(this.selectedFile.folder_resource_id, this.selectedFile.id))
            : this.store.dispatch(
                new Folders.DeleteDocument(this.selectedFile.folder_resource_id, this.selectedFile.id, document.id),
              ),
        ),
      )
      .subscribe(() => {
        if (isLastDocument) {
          this.discard();
        } else {
          this.loadFile();
        }
      });
  }

  deleteFile(): void {
    this.confirmService
      .openConfirmationModal()
      .pipe(
        untilComponentDestroyed(this),
        filter((result: boolean) => result),
        switchMap(() =>
          this.store.dispatch(new Folders.DeleteFile(this.selectedFile.folder_resource_id, this.selectedFile.id)),
        ),
      )
      .subscribe(() => {
        this.discard();
      });
  }

  onFileChanged(event: any) {
    const document: File = event.target.files[0];
    this.store
      .dispatch(new Folders.CreateFileDocument(this.selectedFile.folder_resource_id, this.selectedFile.id, document))
      .pipe(untilComponentDestroyed(this))
      .subscribe(() => {
        event.target.value = '';
        this.loadFile();
      });
  }

  openOnline(document) {
    window.open(document?.links?.edit?.link?.webUrl, '_blank');
  }

  discard(): void {
    this.matDialogRef.close();
  }

  trackByFn(index: number, item: any): any {
    return item.id || index;
  }
}
